/**
 * CWR 1.2 — the Home header, driven by the per-tenant `headerMode`.
 *
 *   GREETING (default) → avatar + time-aware greeting + "Member since …"
 *                        (mockup dashboard-mockup.html:1692–1705).
 *   LOGO + logoUrl is owned by ClientLogoLockupHeader in the shell.
 *   LOGO + null logoUrl → centered text wordmark from business.name, with the
 *                        descriptor line under it when set (fail-soft — never
 *                        an empty header).
 *   NAME                → the same wordmark, never the logo (Gabriel 2026-11-03:
 *                        "just have her name up top there and then the studio").
 *
 * Data comes from the 1.1 dashboard payload (user + business) the ClientShell
 * already owns — this component renders, it does not fetch.
 */
import { useLayoutEffect, useRef } from 'react';
import { InitialsAvatar } from '@/components/hub/InitialsAvatar';
import { formatGreeting } from '@/utils/greeting';
import type { DashboardUser, DashboardBusiness } from '@/types/dashboard';
import styles from './ClientGreetingHeader.module.css';

export interface ClientGreetingHeaderProps {
  user: DashboardUser;
  business: DashboardBusiness;
}

/** The wordmark's size in the stylesheet; the name is drawn smaller only when it would not fit on one line. */
const WORDMARK_PX = 32;

/** Keeps the studio's name on one line (Gabriel 2026-11-03): measured at full size, scaled down to the header's width. */
function useOneLineWordmark(name: string, headerMode: DashboardBusiness['headerMode']) {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const mark = ref.current;
    const box = mark?.parentElement;
    if (!mark || !box) return;
    let disposed = false;

    const fit = () => {
      if (disposed) return;
      mark.style.fontSize = '';
      const natural = mark.getBoundingClientRect().width;
      const room = box.clientWidth;
      if (room > 0 && natural > room) {
        mark.style.fontSize = `${Math.floor((WORDMARK_PX * room * 10) / natural) / 10}px`;
      }
    };

    fit();
    // The studio's heading font can arrive after the first draw and change the name's width.
    void (document.fonts?.ready ?? Promise.resolve()).then(fit);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(box);
    return () => {
      disposed = true;
      observer?.disconnect();
    };
  }, [name, headerMode]);

  return ref;
}

/** "Member since Oct 2024" from an ISO date. */
function formatMemberSince(isoDate: string): string {
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
  return `Member since ${formatter.format(new Date(isoDate))}`;
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
    </svg>
  );
}

export function ClientGreetingHeader({ user, business }: ClientGreetingHeaderProps) {
  const wordmarkRef = useOneLineWordmark(business.name, business.headerMode);

  if (business.headerMode === 'LOGO' || business.headerMode === 'NAME') {
    return (
      <div className={styles.logoHeader} data-testid="client-header-logo">
        <span ref={wordmarkRef} className={styles.wordmark} data-testid="client-header-wordmark">
          {business.name}
        </span>
        {business.descriptor ? (
          <span className={styles.descriptor} data-testid="client-header-descriptor">{business.descriptor}</span>
        ) : null}
      </div>
    );
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.firstName;

  return (
    <div className={styles.greetingHeader} data-testid="client-header-greeting">
      <InitialsAvatar name={fullName} photoUrl={user.avatarUrl} size="sm" />
      <div className={styles.greetingBlock}>
        <h1 className={styles.greeting}>{formatGreeting(user.firstName)}</h1>
        <p className={styles.memberSince}>
          <StarIcon />
          {formatMemberSince(user.memberSince)}
        </p>
      </div>
    </div>
  );
}
